import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSetting, setSetting, settingKeys } from '../../../utils/settings';
import { SMART_SCROLL_CHANGED_EVENT } from '../../../hooks/useSmartScrollSchedule';
import { PLAYING_PART_CHANGED_EVENT, isPlayingPartHighlightEnabled } from '../../../hooks/usePlayingPartHighlight';
import { isCalibrationUser, TIMING_RECORDER_CHANGED_EVENT } from '../../../utils/timingRecorder';
import { SMART_SCROLL_DEBUG_CHANGED_EVENT } from '../../../utils/smartScrollDebug';
import ToggleSwitch from '../../common/ToggleSwitch';
import SettingsModal from '../../common/SettingsModal';
import ModalButton from '../../common/ModalButton';
import { docFor, smartScrollDoc } from '../../../content/details';


const AutoScrollSettingsModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const [lyricsTapEnabled, setLyricsTapEnabled] = useState(() => {
    const stored = getSetting(settingKeys.autoScrollLyricsTap);
    return stored !== undefined ? stored : false; // Default to false (OFF)
  });

  const [rememberSpeed, setRememberSpeed] = useState(() => {
    const stored = getSetting(settingKeys.rememberScrollSpeed);
    return stored !== undefined ? stored : true; // Default to true (ON)
  });

  const [smartScroll, setSmartScroll] = useState(() => {
    const stored = getSetting(settingKeys.smartScroll);
    return stored !== undefined ? stored : false; // Default to false (OFF)
  });

  // Off by default while it's tried out
  const [highlightPart, setHighlightPart] = useState(isPlayingPartHighlightEnabled);

  const [timingRecorder, setTimingRecorder] = useState(() => Boolean(getSetting(settingKeys.timingRecorder)));

  const [smartScrollDebug, setSmartScrollDebug] = useState(() => Boolean(getSetting(settingKeys.smartScrollDebug)));

  // The calibration instruments are development-only and admin-only both — see isCalibrationUser.
  // Read once per open rather than at module load, because the user arrives after the import.
  const showCalibration = isCalibrationUser();

  const handleLyricsTapToggle = () => {
    const newValue = !lyricsTapEnabled;
    setLyricsTapEnabled(newValue);
    setSetting(settingKeys.autoScrollLyricsTap, newValue);
  };

  const handleRememberSpeedToggle = () => {
    const newValue = !rememberSpeed;
    setRememberSpeed(newValue);
    setSetting(settingKeys.rememberScrollSpeed, newValue);
  };

  const handleSmartScrollToggle = () => {
    const newValue = !smartScroll;
    setSmartScroll(newValue);
    setSetting(settingKeys.smartScroll, newValue);
    // The View behind this modal is already mounted, so it needs telling: the schedule has to be
    // built (or dropped) and every scroll control has to swap which mode it is showing
    window.dispatchEvent(new Event(SMART_SCROLL_CHANGED_EVENT));
  };

  const handleHighlightPartToggle = () => {
    const newValue = !highlightPart;
    setHighlightPart(newValue);
    setSetting(settingKeys.highlightPlayingPart, newValue);
    window.dispatchEvent(new Event(PLAYING_PART_CHANGED_EVENT));
  };

  const handleTimingRecorderToggle = () => {
    const newValue = !timingRecorder;
    setTimingRecorder(newValue);
    setSetting(settingKeys.timingRecorder, newValue);
    window.dispatchEvent(new Event(TIMING_RECORDER_CHANGED_EVENT));
  };

  const handleSmartScrollDebugToggle = () => {
    const newValue = !smartScrollDebug;
    setSmartScrollDebug(newValue);
    setSetting(settingKeys.smartScrollDebug, newValue);
    window.dispatchEvent(new Event(SMART_SCROLL_DEBUG_CHANGED_EVENT));
  };

  return (
    <SettingsModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('dialogs.autoScroll.title')}
      className="AutoScrollSettingsModal"
      footer={<ModalButton onClick={onClose}>{t('common.done')}</ModalButton>}
    >
      <ToggleSwitch
        name="smart-scroll"
        label={t('dialogs.autoScroll.smartScroll')}
        checked={smartScroll}
        onChange={handleSmartScrollToggle}
        description={t('dialogs.autoScroll.smartScrollHint')}
        details={docFor(smartScrollDoc, i18n.language)}
        showOnOffText={true}
      />
      {/* Only Smart Scroll knows which part is playing, so without it this reads off and can't be
          switched. The choice is kept for when Smart Scroll comes back on. */}
      <ToggleSwitch
        name="highlight-playing-part"
        label={t('dialogs.autoScroll.highlightPart')}
        checked={smartScroll && highlightPart}
        disabled={!smartScroll}
        onChange={handleHighlightPartToggle}
        description={t('dialogs.autoScroll.highlightPartHint')}
        showOnOffText={true}
      />
      <ToggleSwitch
        name="lyrics-tap"
        label={t('dialogs.autoScroll.lyricsTap')}
        checked={lyricsTapEnabled}
        onChange={handleLyricsTapToggle}
        description={t('dialogs.autoScroll.lyricsTapHint')}
        showOnOffText={true}
      />
      {/* Calibration instruments, not features. Present only in a development build and only for
          an admin — see isCalibrationUser — but reachable from the ordinary settings UI within one,
          because the timings have to be recorded on the same device the song is performed from. */}
      {showCalibration && (
        <ToggleSwitch
          name="timing-recorder"
          label="Part Timer (dev)"
          checked={timingRecorder}
          onChange={handleTimingRecorderToggle}
          description="Testing tool: shows a big button in the song view to tap at each part change, recording how long every part really takes so Smart Scroll's estimates can be calibrated against it."
          showOnOffText={true}
        />
      )}
      {showCalibration && (
        <ToggleSwitch
          name="smart-scroll-debug"
          label="Smart Scroll X-Ray (dev)"
          checked={smartScrollDebug}
          onChange={handleSmartScrollDebugToggle}
          description="Testing tool: tints every part by how fast Smart Scroll is taking it, and labels each one with where its bars came from and what the schedule made of them."
          showOnOffText={true}
        />
      )}
      <ToggleSwitch
        name="remember-speed"
        label={t('dialogs.autoScroll.rememberSpeed')}
        checked={rememberSpeed}
        onChange={handleRememberSpeedToggle}
        description={smartScroll ? t('dialogs.autoScroll.rememberPaceHint') : t('dialogs.autoScroll.rememberSpeedHint')}
        showOnOffText={true}
      />
    </SettingsModal>
  );
};

export default AutoScrollSettingsModal;
